import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigurationSection } from "@features/settings/pages/StyleComponents";
import { BodyLarge, BodyRegular, BodySmall, BodyXXSmall } from "../../atoms/fonts/Body";
import Colours from "../../atoms/Colours";
import { Switch } from "../../atoms/Switch";
import {
  addSignupDomain,
  listSignupDomains,
  removeSignupDomain,
  requestDomainNameSignUp,
  SignupDomain,
} from '@features/settings/services/CompanyService';
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser, updateCurrentUser } from '@shared/store/auth.duck';
import { isCompanyAdminUser } from '@shared/utils/AccessControl';
import { failureNotification } from '@shared/store/notification.duck';
import { SimpleSpinner } from "../../atoms/SimpleSpinner";
import { FlatContentCard } from "../../atoms/FlatContentCard";
import { Trans, useTranslation } from "react-i18next";
import { GreenPill } from "../../atoms/Pill";
import { HeadlineSmall } from "../../atoms/fonts/Headline";
import styled from "styled-components";
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton';
import { OutlineButton } from '../../atoms/buttons/OutlineButton';
import { ButtonSize } from '../../atoms/buttons/ButtonStyling';

/**
 * Domain-based sign-up. The switch is the master on/off; below it the company lists the email
 * domains whose new sign-ups should auto-join. Each domain waits for Team Today staff approval.
 * Company Admins only: the backend rejects everyone else, so nobody else sees the section.
 */
export function DomainBasedSignUpSetting(props: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const {t} = useTranslation();

  const isCompanyAdmin = isCompanyAdminUser(user);
  const enabled = !!user?.companyEntity.domainNameSignupsEnabled;

  const [domains, setDomains] = useState<SignupDomain[] | undefined>(undefined);
  const [newDomain, setNewDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDomains = useCallback(async () => {
    try {
      setDomains(await listSignupDomains());
    } catch (e) {
      setDomains([]);
    }
  }, []);

  useEffect(() => {
    if (isCompanyAdmin) {
      void loadDomains();
    }
  }, [loadDomains, isCompanyAdmin]);

  const approvedDomains = useMemo(() => (domains ?? []).filter(d => d.status === 'APPROVED').map(d => d.domain), [domains]);
  const hasPending = useMemo(() => (domains ?? []).some(d => d.status === 'PENDING'), [domains]);
  const isActive = enabled && approvedDomains.length > 0;
  const isPending = enabled && hasPending && !isActive;

  const onDomainNameChange = async (value: boolean) => {
    try {
      setIsLoading(true);
      await requestDomainNameSignUp(value);
      dispatch(updateCurrentUser(true));
      // Turning the switch on for the first time auto-adds the admin's own domain as pending
      await loadDomains();
    } catch (e: any) {
      dispatch(failureNotification('Failed to update setting, please wait a minute.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    const value = newDomain.trim();
    if (!value) return;
    setError(null);
    setBusy(true);
    try {
      const created = await addSignupDomain(value);
      setDomains(prev => prev ? [...prev, created] : [created]);
      setNewDomain('');
    } catch (err: any) {
      setError(err?.message || t('settings.domain-based-signup.add-failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (row: SignupDomain) => {
    if (!window.confirm(t('settings.domain-based-signup.remove-confirm', { domain: row.domain }))) return;
    setError(null);
    setBusy(true);
    try {
      await removeSignupDomain(row.id);
      setDomains(prev => prev ? prev.filter(d => d.id !== row.id) : prev);
    } catch (err: any) {
      setError(err?.message || t('settings.domain-based-signup.remove-failed'));
    } finally {
      setBusy(false);
    }
  };

  if (!isCompanyAdmin) {
    return null;
  }

  return (
    <ConfigurationSection className={'domainBasedSignup__section'}>
      <GreenPill style={{marginBottom: 16}}><BodyXXSmall>{t('settings.domain-based-signup.everyone')}</BodyXXSmall></GreenPill>
      <SwitchWrapper>
        {isLoading ? <SimpleSpinner size={32} /> : <Switch value={enabled} label="" onChange={onDomainNameChange}/>}
      </SwitchWrapper>
      {!!props.settingsView ? <BodyLarge colour={Colours.blue} style={{marginBottom: 16}}>{t('settings.domain-based-signup.title')}</BodyLarge> : <HeadlineSmall style={{marginBottom: 16}}>{t('settings.domain-based-signup.title')}</HeadlineSmall>}
      <BodyRegular style={{marginBottom: 4}} weight={600}>{t('settings.domain-based-signup.subtitle')}</BodyRegular>
      <BodyRegular style={{marginBottom: 16}}>{
        isActive
          ? (approvedDomains.length === 1
              ? <Trans i18nKey={'settings.domain-based-signup.enabled-message'} components={[<strong>placeholder</strong>]} values={{domainName: approvedDomains[0]}} />
              : <Trans i18nKey={'settings.domain-based-signup.enabled-message-multiple'} components={[<strong>placeholder</strong>]} values={{domains: approvedDomains.join(', ')}} />)
          : <Trans i18nKey={'settings.domain-based-signup.disabled-message'} components={[<strong>{t('settings.domain-based-signup.your-domain-name')}</strong>]} />
      }</BodyRegular>

      {!props.hideExtraInfo && <>
        <BodyRegular style={{ marginBottom: 4 }} weight={600}>{t('settings.domain-based-signup.domains-title')}</BodyRegular>
        <BodyRegular style={{ marginBottom: 12, color: Colours.darkGrey }}>{t('settings.domain-based-signup.approval-note')}</BodyRegular>

        <InlineRow>
          <Input
            type="text"
            maxLength={253}
            placeholder={t('settings.domain-based-signup.placeholder')}
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void handleAdd(); } }}
            disabled={busy}
            data-test="signupDomainInput"
          />
          <PrimaryButton text={t('settings.domain-based-signup.add')}
                         size={ButtonSize.Small}
                         click={handleAdd}
                         disabled={busy || !newDomain.trim()} />
        </InlineRow>
        {error && <ErrorBox>{error}</ErrorBox>}

        {domains === undefined ? (
          <SimpleSpinner size={32} />
        ) : domains.length === 0 ? (
          <BodySmall style={{ marginTop: 12, marginBottom: 16, color: Colours.darkGrey }}>{t('settings.domain-based-signup.empty')}</BodySmall>
        ) : (
          <List>
            {domains.map(row => (
              <RowWrap key={row.id}>
                <RowMain>
                  <DomainValue>{row.domain}</DomainValue>
                  {row.status === 'APPROVED' && <Pill $variant="ok">{t('settings.domain-based-signup.status-active')}</Pill>}
                  {row.status === 'PENDING' && <Pill $variant="pending">{t('settings.domain-based-signup.status-pending')}</Pill>}
                  {row.status === 'REJECTED' && <Pill $variant="danger">{t('settings.domain-based-signup.status-rejected')}</Pill>}
                </RowMain>
                {row.status === 'REJECTED' && (
                  <BodySmall style={{ color: Colours.darkGrey }}>{t('settings.domain-based-signup.rejected-help')}</BodySmall>
                )}
                <OutlineButton text={t('settings.domain-based-signup.remove')}
                               size={ButtonSize.Small}
                               click={() => handleRemove(row)}
                               disabled={busy} />
              </RowWrap>
            ))}
          </List>
        )}

        {!enabled && (domains?.length ?? 0) > 0 && (
          <BodySmall style={{ marginBottom: 16, color: Colours.darkGrey }}>{t('settings.domain-based-signup.switch-off-note')}</BodySmall>
        )}

        {isPending &&
          <FlatContentCard>
            <BodyLarge colour={Colours.blue}>{t('settings.domain-based-signup.pending-line-1')}</BodyLarge>
            <BodyRegular style={{marginBottom: 16}}>{t('settings.domain-based-signup.pending-line-2')}</BodyRegular>
            <BodyRegular style={{marginBottom: 16}}>{t('settings.domain-based-signup.pending-line-3')}</BodyRegular>
            <BodyRegular>{t('settings.domain-based-signup.pending-line-4')}</BodyRegular>
          </FlatContentCard>}

        {isActive &&
          <FlatContentCard>
            <BodyLarge colour={Colours.blue}>{t('settings.domain-based-signup.active-line-1')}</BodyLarge>
            <BodyRegular style={{marginBottom: 16}}>{t('settings.domain-based-signup.active-line-2')}</BodyRegular>
            <BodyRegular style={{marginBottom: 16}}>{t('settings.domain-based-signup.active-line-3')}</BodyRegular>
          </FlatContentCard>}
      </>}

    </ConfigurationSection>
  )
}

interface Props {
  hideExtraInfo?: boolean;
  settingsView?: boolean;
}

const SwitchWrapper = styled.div`
  position: absolute;
  top: 40px;
  right: 8px;
`

const InlineRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Input = styled.input`
  height: 32px;
  padding: 0 10px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-size: 14px;
  min-width: 240px;
  flex: 1;
  max-width: 400px;
`;

const ErrorBox = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 4px;
  font-size: 13px;
`;

const List = styled.div`
  margin: 12px 0 16px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RowWrap = styled.div`
  padding: 10px 12px;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  & > button {
    margin-left: auto;
  }
`;

const RowMain = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DomainValue = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  font-weight: 600;
`;

const Pill = styled.span<{ $variant: 'ok' | 'pending' | 'danger' }>`
  font-family: proxima-nova;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${p => p.$variant === 'ok' ? Colours.green : (p.$variant === 'danger' ? Colours.red : Colours.orange20)};
  color: ${p => p.$variant === 'pending' ? Colours.darkGrey : 'white'};
`;
